import { useState, type FC, useEffect, type ReactNode, ComponentProps } from 'react';
import { observer } from 'mobx-react-lite';
import { Form, type TableProps } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Modal, Space } from '@g2u/ui-kit';

import { RecordLockType, type Customer, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { ConfirmCustomerFile, SearchCustomer } from 'components';
import { BookingStore } from 'store';
import { useCheckLock } from 'hooks/useCheckLock';

type Props = TableProps<any> & {
  hasDiscount?: boolean;
  hasScript?: boolean;
  isOpen: boolean;
  onOk: () => void;
  onCancel: () => void;
  actions: ComponentProps<typeof SearchCustomer>['actions'];
  customer?: Customer | null;
  modalTitle: ReactNode | string;
  emptyContent?: ComponentProps<typeof SearchCustomer>['emptyContent'];
} & WithOptionalDataTest;

export const CustomerSearchModal: FC<Props> = observer((props) => {
  const { onCancel, isOpen, onOk, hasScript, actions, emptyContent, customer, modalTitle, dataTestId, hasDiscount } =
    props;
  const [showCustomerSearchModal, setShowCustomerSearchModal] = useState<boolean>(isOpen);
  const [isCustomerUpdating, setIsCustomerUpdating] = useState<boolean>(false);
  const { isLocked } = useCheckLock({
    recordId: customer?.ID ? Number(customer?.ID) : undefined,
    recordLockType: RecordLockType.Customer,
  });
  const { setCustomer } = BookingStore;
  const [form] = Form.useForm();
  Form.useWatch([], { form });

  useEffect(() => {
    setShowCustomerSearchModal(isOpen);
  }, [isOpen]);

  const handleProfileUpdating = (status: boolean) => {
    setIsCustomerUpdating(status);
  };

  const handleProfileUpdated = (updatedProfile: JsonDataItem | null) => {
    updatedProfile && setCustomer(updatedProfile);
    setShowCustomerSearchModal(false);
    onOk();
  };

  return (
    <Modal.$
      wide
      footer={customer ? undefined : null}
      open={showCustomerSearchModal}
      destroyOnClose
      maskClosable={false}
      title={modalTitle}
      okText="Start a new booking"
      cancelText="&lt; Back"
      onOk={() => {
        form.submit();
      }}
      okButtonProps={{
        loading: isCustomerUpdating,
        disabled: isLocked,
      }}
      onCancel={() => {
        customer ? setCustomer(null) : onCancel();
      }}
      cancelButtonProps={{
        disabled: isCustomerUpdating,
      }}
      closeIcon={
        <CloseOutlined
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            customer ? setCustomer(null) : onCancel();
          }}
        />
      }
      dataTestId={dataTestId ?? 'customer-search-modal'}
    >
      {!customer ? (
        <SearchCustomer
          hasScript={hasScript}
          actions={actions}
          emptyContent={
            emptyContent
              ? (formValues) => (
                  <Space.$ align={Space.Align.Center} direction={Space.Direction.Vertical}>
                    {emptyContent(formValues)}
                  </Space.$>
                )
              : undefined
          }
        />
      ) : (
        <ConfirmCustomerFile
          hasScript
          hasDiscount={hasDiscount}
          customer={customer}
          form={form}
          onUpdated={handleProfileUpdated}
          onLoading={handleProfileUpdating}
          isLocked={isLocked}
        />
      )}
    </Modal.$>
  );
});
